import Constants from '@/libs/Constants';
import cookieSetterGetter from "@/libs/Cookie";

class ApiConnector {
    constructor(apiUrl) {
        if (typeof apiUrl !== 'string')
            apiUrl = '';

        // Use the environment variable for the API URL
        if (apiUrl.endsWith('/') === true)
            apiUrl = apiUrl.substring(0, apiUrl.length - 1);

        this.apiUrl = apiUrl;

        this.statusCodeMap = {
            400: Constants.STATUS.BAD_REQUEST,
            401: Constants.STATUS.UNAUTHORIZED,
            403: Constants.STATUS.ACCESS_FORBIDDEN,
            404: Constants.STATUS.NOT_FOUND,
            500: Constants.STATUS.SERVER_ERROR,
            502: Constants.STATUS.BAD_GATEWAY,
            503: Constants.STATUS.NOT_AVAILABLE
        }
    }

    _url(endpoint) {
        if (typeof endpoint !== 'string')
            endpoint = '';

        if (endpoint.startsWith('/') === false)
            endpoint = `/${endpoint}`

        return `${this.apiUrl}${endpoint}`;
    }

    async _wrapFetch(endpoint, init = {}, expectedStatusCode = 200, timeout = 30000) {
        //Add headers from user
        let user = cookieSetterGetter.getCookie('user_ref');
        if (user !== null) {
            if (init.hasOwnProperty('headers') === false)
                init.headers = {};
            init.headers['x-user-ref'] = user;
        }

        const fetchWithTimeout = new Promise((resolve, reject) => {
            setTimeout(() => reject(new Error(Constants.STATUS.TIMEOUT)), timeout);

            fetch(this._url(endpoint), init).then(response => {
                if (response.ok && response.status === expectedStatusCode) {
                    return response;
                } else {
                    let statusValue = this.statusCodeMap[response.status];
                    statusValue = statusValue !== undefined ? statusValue : Constants.STATUS.UNEXPECTED;
                    reject(new Error(statusValue));
                }
            }).then(resolve).catch(reject)
        });

        try {
            return await fetchWithTimeout;
        } catch (error) {
            if (error.name === 'TypeError')
                throw new Error(Constants.STATUS.NETWORK_ERROR);

            throw new Error(error.message);
        }
    }


    /* ----------
     * Monitoring
     * ---------- */
    async getMonitoringLayers() {
        return this._wrapFetch('/monitoring/layers', {
            method: 'GET'
        });
    }

    async postMonitoringQuery(layer_id, year_month) {
        return this._wrapFetch('/monitoring/query', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                year_month: year_month
            })
        });
    }

    async postMonitoringTimeseries(layer_id, start_year_month, end_year_month, features = []) {
        return this._wrapFetch('/monitoring/timeseries', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                start_year_month: start_year_month,
                end_year_month: end_year_month,
                feature_ids: features
            })
        });
    }

    async postMonitoringTimeseriesDownload(layer_id, start_year_month, end_year_month, features = []) {
        return this._wrapFetch('/monitoring/timeseries/download', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                start_year_month: start_year_month,
                end_year_month: end_year_month,
                feature_ids: features
            })
        });
    }

    /* --------
     * Forecast
     * -------- */
    async getForecastLayers() {
        return this._wrapFetch('/forecast/layers', {method: 'GET'});
    }

    async postForecastQuery(layer_id, percentile, year_month, step) {
        return this._wrapFetch('/forecast/query', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                percentile: percentile,
                creation_year_month: year_month,
                forecast_year_month: step
            })
        });
    }

    async postForecastTimeseries(layer_id, creation_year_month, features = []) {
        return this._wrapFetch('/forecast/timeseries', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                creation_year_month: creation_year_month,
                feature_ids: features
            })
        });
    }

    async postForecastTimeseriesDownload(layer_id, creation_year_month, features = []) {
        return this._wrapFetch('/forecast/timeseries/download', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                creation_year_month: creation_year_month,
                feature_ids: features
            })
        });
    }

    /* ----------
     * Projection
     * ---------- */
    async getProjectionLayers() {
        return this._wrapFetch('/projection/layers', {
            method: 'GET'
        });
    }

    async postProjectionQuery(layer_id, season, year, scenario) {
        return this._wrapFetch('/projection/query', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                season: season,
                year: year,
                ssp: scenario
            })
        });
    }

    async postProjectionTimeseries(layer_id, features = []) {
        return this._wrapFetch('/projection/timeseries', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                feature_ids: features
            })
        });
    }

    async postProjectionTimeseriesDownloadAnnual(layer_id, features = []) {
        return this._wrapFetch('/projection/timeseries/download/annual', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                feature_ids: features
            })
        });
    }

    async postProjectionTimeseriesDownloadMonthly(layer_id, year, features = []) {
        return this._wrapFetch('/projection/timeseries/download/monthly', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                year: year,
                feature_ids: features
            })
        });
    }

    /* ----------
     * Simulation
     * ---------- */
    async getSimulationLayers() {
        return this._wrapFetch('/simulation/layers', {
            method: 'GET'
        });
    }

    async postSimulationRun(creationMonth,
                            snowWaterPercentile,
                            precipitationTemperatureType,
                            precipitationTemperaturePercentile,
                            soilWaterPercentile,
                            derivations,
                            damLakes) {
        let data = {
            creation_month: creationMonth,
            snow_water_percentile: snowWaterPercentile,
            precipitation_temperature_type: precipitationTemperatureType,
            precipitation_temperature_percentile: precipitationTemperaturePercentile,
            soil_water_percentile: soilWaterPercentile,
            derivations: derivations,
            dam_lakes: damLakes
        };

        return this._wrapFetch('/simulation/run', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(data)
        });
    }

    async getSimulationStatus(id) {
        return this._wrapFetch(`/simulation/status?id=${id}`, {
            method: 'GET'
        });
    }

    async postSimulationQuery(simulation_id, layer_id, forecast_month) {
        return this._wrapFetch('/simulation/query', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                variable_id: layer_id,
                simulation_id: simulation_id,
                forecast_month: forecast_month
            })
        });
    }

    async postSimulationTimeseries(simulation_id, layer_id, features = []) {
        return this._wrapFetch('/simulation/timeseries', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                simulation_id: simulation_id,
                variable_id: layer_id,
                feature_ids: features
            })
        });
    }

    async postSimulationTimeseriesDownload(simulation_id, layer_id, features = []) {
        return this._wrapFetch('/simulation/timeseries/download', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                simulation_id: simulation_id,
                variable_id: layer_id,
                feature_ids: features
            })
        });
    }

    /* -----
     * Users
     * ----- */
    async putUserRegister(email, fullName, institution) {
        return this._wrapFetch('/users/register', {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email: email,
                    name: fullName,
                    institution: institution
                })
            },
            201);
    }

    async postUserFeedback(email, section, categories, comment) {
        return this._wrapFetch('/users/feedback', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    email: email,
                    section: section,
                    categories: categories,
                    comment: comment
                })
            },
            201);
    }

    async getConfigurationLayers(layer) {
        return this._wrapFetch(`/configuration/layer?name=${layer}`, {
            method: 'GET'
        });
    }
}

// Use the environment variable for the API URL
export default new ApiConnector(process.env.VUE_APP_API_URL);
